import { cellKey, stepCell, type Cell, type Direction, type FlipwallLine } from './types';

export interface FlipwallRules {
  isInvalid(cell: Cell): boolean;
  isOccupied(cell: Cell): boolean;
  isSafe(cells: readonly Cell[]): boolean;
}

export interface PlacementResult {
  accepted: boolean;
  line: FlipwallLine | null;
  removed: FlipwallLine[];
  reason: 'ok' | 'empty' | 'unsafe' | 'limit';
}

export class FlipwallSystem {
  readonly maxLinesPerPlayer: number;
  readonly durationTicks: number;
  /** Fixed steps each segment waits before it appears as the line is exhaled. */
  readonly growTicksPerCell: number;
  lines: FlipwallLine[] = [];
  revision = 0;
  private nextId = 1;

  /**
   * Frostwalls follow the arcade rule: the action key builds a line, and ice
   * only melts when a cat presses again while facing it. Lines therefore
   * persist (`durationTicks` of 0) and a cat may hold many of them at once —
   * placing a second line must never dissolve the first.
   */
  constructor(maxLinesPerPlayer = 12, durationTicks = 0, growTicksPerCell = 6) {
    this.maxLinesPerPlayer = maxLinesPerPlayer;
    this.durationTicks = durationTicks;
    this.growTicksPerCell = growTicksPerCell;
  }

  preview(origin: Cell, direction: Direction, rules: FlipwallRules, maxLength = 64): Cell[] {
    const cells: Cell[] = [];
    let cursor = origin;
    for (let index = 0; index < maxLength; index += 1) {
      cursor = stepCell(cursor, direction);
      if (rules.isInvalid(cursor) || rules.isOccupied(cursor) || this.hasCell(cursor)) break;
      cells.push(cursor);
    }
    return cells;
  }

  place(owner: 1 | 2, cells: readonly Cell[], tick: number, rules: FlipwallRules): PlacementResult {
    if (cells.length === 0) return { accepted: false, line: null, removed: [], reason: 'empty' };
    if (this.lines.filter((line) => line.owner === owner).length >= this.maxLinesPerPlayer) {
      return { accepted: false, line: null, removed: [], reason: 'limit' };
    }
    if (!rules.isSafe(cells)) return { accepted: false, line: null, removed: [], reason: 'unsafe' };
    const permanent = this.durationTicks <= 0;
    const line: FlipwallLine = {
      id: this.nextId,
      owner,
      cells: cells.map((cell) => ({ ...cell })),
      createdTick: tick,
      expiresTick: permanent ? Number.POSITIVE_INFINITY : tick + this.durationTicks,
      warningTick: permanent ? Number.POSITIVE_INFINITY : tick + this.durationTicks - 120,
      growTicks: cells.length * this.growTicksPerCell,
    };
    this.nextId += 1;
    this.lines.push(line);
    this.revision += 1;
    return { accepted: true, line, removed: [], reason: 'ok' };
  }

  expire(tick: number): FlipwallLine[] {
    const expired = this.lines.filter((line) => line.expiresTick <= tick);
    if (expired.length === 0) return [];
    const ids = new Set(expired.map((line) => line.id));
    this.lines = this.lines.filter((line) => !ids.has(line.id));
    this.revision += 1;
    return expired;
  }

  shatter(owner: 1 | 2): { lines: FlipwallLine[]; cells: Cell[] } {
    const removed = this.lines.filter((line) => line.owner === owner);
    if (removed.length === 0) return { lines: [], cells: [] };
    const ids = new Set(removed.map((line) => line.id));
    this.lines = this.lines.filter((line) => !ids.has(line.id));
    this.revision += 1;
    return { lines: removed, cells: removed.flatMap((line) => line.cells) };
  }

  /**
   * The unbroken run of ice starting at `origin` and extending in `direction`.
   *
   * Melting takes exactly this run, so standing at the end of a line clears the
   * whole line while standing beside it clears only the block you face. The run
   * ignores which line each block belongs to — what matters is the shape of the
   * ice in front of the cat, not how it was built.
   */
  runFrom(origin: Cell, direction: Direction, maxLength = 64): Cell[] {
    const cells: Cell[] = [];
    let cursor = origin;
    for (let index = 0; index < maxLength; index += 1) {
      if (!this.hasCell(cursor)) break;
      cells.push({ ...cursor });
      cursor = stepCell(cursor, direction);
    }
    return cells;
  }

  /** The line occupying a cell, so a cat can melt exactly what it faces. */
  lineAt(cell: Cell): FlipwallLine | null {
    const key = cellKey(cell);
    return this.lines.find((line) => line.cells.some((candidate) => cellKey(candidate) === key)) ?? null;
  }

  /** Removes one line by id, leaving every other wall standing. */
  removeLine(id: number): { lines: FlipwallLine[]; cells: Cell[] } {
    const removed = this.lines.filter((line) => line.id === id);
    if (removed.length === 0) return { lines: [], cells: [] };
    this.lines = this.lines.filter((line) => line.id !== id);
    this.revision += 1;
    return { lines: removed, cells: removed.flatMap((line) => line.cells) };
  }

  /** Removes every temporary line without creating a player shatter pulse. */
  clear(): { lines: FlipwallLine[]; cells: Cell[] } {
    if (this.lines.length === 0) return { lines: [], cells: [] };
    const removed = this.lines;
    this.lines = [];
    this.revision += 1;
    return { lines: removed, cells: removed.flatMap((line) => line.cells) };
  }

  /**
   * Dissolves only portions occupying the supplied cells. A queued fruit
   * family can then bloom safely while unrelated player defenses stay put.
   */
  clearCells(cellsToClear: readonly Cell[]): Cell[] {
    const keys = new Set(cellsToClear.map(cellKey));
    if (keys.size === 0) return [];
    const removed: Cell[] = [];
    this.lines = this.lines.flatMap((line) => {
      const cells = line.cells.filter((candidate) => {
        if (!keys.has(cellKey(candidate))) return true;
        removed.push({ ...candidate });
        return false;
      });
      return cells.length > 0 ? [{ ...line, cells }] : [];
    });
    if (removed.length > 0) this.revision += 1;
    return removed;
  }

  removeCell(cell: Cell): boolean {
    let changed = false;
    this.lines = this.lines.flatMap((line) => {
      const cells = line.cells.filter((candidate) => cellKey(candidate) !== cellKey(cell));
      if (cells.length === line.cells.length) return [line];
      changed = true;
      return cells.length > 0 ? [{ ...line, cells }] : [];
    });
    if (changed) this.revision += 1;
    return changed;
  }

  hasCell(cell: Cell): boolean {
    const key = cellKey(cell);
    return this.lines.some((line) => line.cells.some((candidate) => cellKey(candidate) === key));
  }

  ownerLineCount(owner: 1 | 2): number {
    return this.lines.filter((line) => line.owner === owner).length;
  }
}
